/* ---------------------------------------------------------------------------
 * Pattern generator — email gate and kit handoff.
 *
 * Loaded (deferred) immediately after pattern-generator.js and shares its
 * top-level bindings: uploadedImage, patternData, patternCanvas, previewSection,
 * emailGate, unlockedContent, uploadPreview, uploadContent, generateBtn,
 * isUnlocked, generatePattern(), updateWidthLabel(), updateColorsLabel().
 * ------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
 * Kit handoff
 *
 * The "order a kit" CTA used to dump the customer on a bare PDP where they had
 * to re-pick a size and re-upload the photo they'd just used. We now recommend
 * the kit size matching their pattern's shape and hand the preview across.
 * ------------------------------------------------------------------------- */

// Label must match the product's Size option values exactly.
const KIT_SIZES = [
  { label: '8" x 12" (20cm x 30cm)', w: 8, h: 12 },
  { label: '12" x 12" (30cm x 30cm)', w: 12, h: 12 },
  { label: '12" x 16" (30cm x 40cm)', w: 12, h: 16 },
  { label: '16" x 16" (40cm x 40cm)', w: 16, h: 16 },
  { label: '16" x 20" (40cm x 50cm)', w: 16, h: 20 },
  { label: '16" x 24" (40cm x 60cm)', w: 16, h: 24 },
  { label: '20" x 20" (50cm x 50cm)', w: 20, h: 20 },
  { label: '24" x 24" (60cm x 60cm)', w: 24, h: 24 },
  { label: '24" x 32" (60cm x 80cm)', w: 24, h: 32 }
];

// ponytail: match on aspect ratio only. Kit size sets the fabric, and the
// pattern is rescaled to it, so absolute inches don't constrain the choice.
function recommendKitSize(stitchWidth, stitchHeight) {
  const ratio = Math.max(stitchWidth, stitchHeight) / Math.min(stitchWidth, stitchHeight);
  return KIT_SIZES.reduce((best, size) => {
    const sizeRatio = Math.max(size.w, size.h) / Math.min(size.w, size.h);
    return Math.abs(sizeRatio - ratio) < Math.abs(best.ratio - ratio)
      ? { ...size, ratio: sizeRatio }
      : best;
  }, { ...KIT_SIZES[2], ratio: 16 / 12 });
}

function updateKitHandoff(stitchWidth, stitchHeight, colorCount) {
  const link = document.getElementById('ctaKitLink');
  if (!link) return;

  const size = recommendKitSize(stitchWidth, stitchHeight);
  const portrait = stitchHeight > stitchWidth;

  const note = document.getElementById('ctaRecommend');
  if (note) {
    note.textContent =
      `Best fit for your design: ${size.label.split(' (')[0]} ${portrait ? 'portrait' : 'landscape'} — ` +
      `${colorCount} DMC colours, all pre-sorted.`;
    note.hidden = false;
  }

  const base = link.getAttribute('href').split('?')[0];
  link.setAttribute('href', `${base}?pg_size=${encodeURIComponent(size.label)}`);

  try {
    sessionStorage.setItem('pg_handoff', JSON.stringify({
      preview: patternCanvas.toDataURL('image/jpeg', 0.8),
      size: size.label,
      stitchWidth,
      stitchHeight,
      colorCount,
      ts: Date.now()
    }));
  } catch (e) {
    console.warn('Could not stash kit handoff:', e);
  }
}
/* ---------------------------------------------------------------------------
 * Email gate
 *
 * The gate is a real {% form 'customer' %}. Shopify's invisible CAPTCHA only
 * attaches its token to genuine form submits, so the old fetch('/contact')
 * approach always failed with HTTP 400 "Missing CAPTCHA token" — and because
 * the error was swallowed, every email collected was silently discarded.
 *
 * A real submit navigates away, so we stash the generator state first and
 * restore it when Shopify redirects back with ?customer_posted=true.
 * ------------------------------------------------------------------------- */

const PG_STASH_KEY = 'pg_state';

function stashGeneratorState() {
  if (!uploadedImage || !patternData) return;
  try {
    // Downscale the source so it comfortably fits sessionStorage (~5MB budget).
    const max = 1000;
    const scale = Math.min(1, max / Math.max(uploadedImage.width, uploadedImage.height));
    const c = document.createElement('canvas');
    c.width = Math.round(uploadedImage.width * scale);
    c.height = Math.round(uploadedImage.height * scale);
    c.getContext('2d').drawImage(uploadedImage, 0, 0, c.width, c.height);

    sessionStorage.setItem(PG_STASH_KEY, JSON.stringify({
      src: c.toDataURL('image/jpeg', 0.85),
      stitchWidth: document.getElementById('stitchWidth').value,
      maxColors: document.getElementById('maxColors').value,
      fabricCount: document.getElementById('fabricCount').value,
      ts: Date.now()
    }));
  } catch (e) {
    // Quota or canvas taint — the gate still works, the user just re-uploads.
    console.warn('Could not stash pattern state:', e);
  }
}

function restoreGeneratorState() {
  let stash;
  try {
    stash = JSON.parse(sessionStorage.getItem(PG_STASH_KEY) || 'null');
  } catch (e) {
    return false;
  }
  if (!stash || !stash.src) return false;

  document.getElementById('stitchWidth').value = stash.stitchWidth;
  document.getElementById('maxColors').value = stash.maxColors;
  document.getElementById('fabricCount').value = stash.fabricCount;
  updateWidthLabel();
  updateColorsLabel();

  const img = new Image();
  img.onload = () => {
    uploadedImage = img;
    uploadPreview.src = stash.src;
    uploadPreview.style.display = 'block';
    uploadContent.style.display = 'none';
    generateBtn.disabled = false;
    generatePattern();
    unlockPattern();
  };
  img.src = stash.src;
  return true;
}

function unlockPattern() {
  isUnlocked = true;
  patternCanvas.classList.remove('preview-blurred');
  emailGate.classList.add('hidden');
  emailGate.style.display = 'none';
  unlockedContent.style.display = 'block';
}

(function initEmailGate() {
  const form = document.getElementById('pgEmailForm');
  if (form) {
    form.addEventListener('submit', () => {
      stashGeneratorState();
      if (typeof gtag !== 'undefined') {
        gtag('event', 'generate_lead', {
          event_category: 'Pattern Generator',
          event_label: 'Email Captured'
        });
      }
    });
  }

  // Returning from a successful Shopify signup — rebuild the pattern and unlock.
  if (new URLSearchParams(location.search).has('customer_posted')) {
    if (restoreGeneratorState()) {
      previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
})();
